import { directionVectorFromDeg } from '@/utils/wind-utils';

// ---------------------------------------------------------------------------
// Public constants (all magic numbers named here per RULE-064)
// ---------------------------------------------------------------------------

export const MIN_GRID_CELL_SIZE_PX = 100;
export const STREAMLET_LINE_WIDTH = 2.0;
export const STREAMLET_MIN_LENGTH_PX = 60;
export const STREAMLET_MAX_LENGTH_PX = 300;
export const STREAMLET_SHADOW_BLUR = 3;
export const STREAMLET_SHADOW_COLOR = 'black';
export const STREAMLET_DASH_PATTERN: [number, number] = [120, 80];
export const STREAMLET_HEAD_ALPHA = 0.4;
export const STREAMLET_FAVORABILITY_ALPHA = 0.8;
export const FAVORABILITY_GLOW_RADIUS_PX = 40;
export const STREAMLET_HEAD_COLOR_BASE = '232,229,224';

/** Viewport fraction used to cap streamlet line length on small screens. */
export const VIEWPORT_LENGTH_CAP_RATIO = 0.25;

/**
 * Full dash cycle length in pixels (dash + gap).
 * Used to seed per-streamlet dashOffset within one cycle for burst-free init.
 */
export const DASH_CYCLE_LENGTH = STREAMLET_DASH_PATTERN[0] + STREAMLET_DASH_PATTERN[1];

// ---------------------------------------------------------------------------
// Grid density helper
// ---------------------------------------------------------------------------

/**
 * Compute the grid cell size in pixels based on viewport dimensions.
 * On large viewports (e.g. 1440×900) this yields larger cells (fewer streamlets).
 * On typical phones (375×667) the 100px floor keeps density reasonable.
 *
 * Formula: Math.max(100, Math.min(width, height) * 0.15)
 */
export function computeGridCellSize(width: number, height: number): number {
  return Math.max(MIN_GRID_CELL_SIZE_PX, Math.min(width, height) * 0.15);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface WindStreamletEngineOptions {
  /** The canvas element to draw on. Must already be in the DOM. */
  canvas: HTMLCanvasElement;
  /** Returns current canvas logical dimensions (used after resize). */
  getViewport: () => { width: number; height: number };
  /** Returns current wind state, or null when unavailable. */
  getWind: () => { degFrom: number; speedMph: number } | null;
  /**
   * @deprecated Grid spawn is automatic. Pass this only for back-compat.
   * When provided, a console.warn is emitted once and the value is ignored.
   */
  streamletCount?: number;
  /**
   * Returns screen-space positions of favorable stands.
   * Called once per frame INSIDE the document-visible guard.
   * Positions are recomputed each call to track pan/zoom accurately.
   */
  getFavorableStandPositions?: () => Array<{ x: number; y: number }>;
}

export interface WindStreamletEngine {
  /** Begin the rAF animation loop. No-op if already running. */
  start(): void;
  /** Cancel the rAF loop and clear the canvas. */
  stop(): void;
  /** Resize the canvas to new pixel dimensions and redistribute streamlets. */
  resize(width: number, height: number): void;
  /** Force re-fetch of wind state on the next frame. */
  updateWind(): void;
  /** Returns true when the animation loop is active. */
  isRunning(): boolean;
  /** Returns the current number of active streamlets. */
  getStreamletCount(): number;
  /**
   * Update the map bearing (degrees clockwise from north).
   * The engine adjusts wind direction rendering so streamlets flow
   * in the correct screen direction after map rotation.
   */
  updateBearing(deg: number): void;
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface Streamlet {
  /** Current x position in canvas pixels — this is the HEAD of the streamlet. */
  x: number;
  /** Current y position in canvas pixels — this is the HEAD of the streamlet. */
  y: number;
  /** Stroke length in pixels. */
  length: number;
  /** Pixels per frame. */
  speed: number;
  /** Pixels traveled since spawning. */
  traveled: number;
  /** Total pixels before despawn. */
  maxTravel: number;
  /**
   * Per-streamlet dash offset in pixels.
   * Initialized to a random value within one dash cycle so all streamlets
   * don't appear simultaneously on activation (no initial burst).
   * Decremented each frame by baseSpeed for the marching-ants animation.
   */
  dashOffset: number;
}

// ---------------------------------------------------------------------------
// Speed curve — preserved verbatim
// ---------------------------------------------------------------------------

/**
 * Convert a wind speed (mph) to a canvas pixels-per-frame base speed using a
 * smoothstep easing curve so very slow/fast winds stay legible.
 *
 * Clamp: [3, 25] mph → eased output: [0.6, 5.0] px/frame.
 * Zero or NaN wind returns 0 (streamlets stay stationary).
 */
export function computeStreamletBaseSpeed(mph: number): number {
  if (!mph || isNaN(mph) || mph <= 0) return 0;
  const MIN_MPH = 3;
  const MAX_MPH = 25;
  const MIN_SPEED = 1.0;
  const MAX_SPEED = 5.0;
  const clamped = Math.max(MIN_MPH, Math.min(MAX_MPH, mph));
  const t = (clamped - MIN_MPH) / (MAX_MPH - MIN_MPH);
  const eased = t * t * (3 - 2 * t); // smoothstep
  return MIN_SPEED + (MAX_SPEED - MIN_SPEED) * eased;
}

// ---------------------------------------------------------------------------
// Line length helper
// ---------------------------------------------------------------------------

/**
 * Map a wind speed (mph) to a streamlet line length in pixels.
 * Linear interpolation between STREAMLET_MIN_LENGTH_PX and STREAMLET_MAX_LENGTH_PX
 * over the usable range [1, 20] mph.
 */
export function computeStreamletLineLength(mph: number): number {
  if (!isFinite(mph) || mph <= 0) return STREAMLET_MIN_LENGTH_PX;
  return Math.min(
    STREAMLET_MAX_LENGTH_PX,
    STREAMLET_MIN_LENGTH_PX + (mph - 1) * (240 / 19),
  );
}

// ---------------------------------------------------------------------------
// Internal constants (not exported — all visual magic numbers are above)
// ---------------------------------------------------------------------------

const SPEED_JITTER_MIN = 0.85;
const SPEED_JITTER_MAX = 1.15;

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createWindStreamletEngine(opts: WindStreamletEngineOptions): WindStreamletEngine {
  const { canvas, getViewport, getWind } = opts;

  // Back-compat: warn and ignore the deprecated streamletCount option
  if (opts.streamletCount !== undefined) {
    console.warn('[wind-streamlet] streamletCount is deprecated; grid spawn is automatic');
  }

  let rafId: number | null = null;
  let running = false;
  let streamlets: Streamlet[] = [];
  /** Current map bearing in degrees clockwise from north. Default 0 (unrotated). */
  let bearing = 0;

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function randomBetween(min: number, max: number): number {
    return min + Math.random() * (max - min);
  }

  function spawnStreamletAtCell(col: number, row: number, stagger: boolean): Streamlet {
    const { width, height } = getViewport();
    const cellSize = computeGridCellSize(width, height);
    const travelBase = Math.max(width, height) * 1.2;
    const x = col * cellSize + Math.random() * cellSize;
    const y = row * cellSize + Math.random() * cellSize;
    const maxTravel = travelBase * randomBetween(0.7, 1.3);
    return {
      x,
      y,
      length: STREAMLET_MIN_LENGTH_PX,
      speed: 1,
      traveled: stagger ? Math.random() * maxTravel : 0,
      maxTravel,
      // Random initial offset within one full dash cycle — prevents initial burst
      dashOffset: Math.random() * DASH_CYCLE_LENGTH,
    };
  }

  function initStreamlets(): void {
    const { width, height } = getViewport();
    const cellSize = computeGridCellSize(width, height);
    const cols = Math.ceil(width / cellSize);
    const rows = Math.ceil(height / cellSize);
    streamlets = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        streamlets.push(spawnStreamletAtCell(col, row, /* stagger */ true));
      }
    }
  }

  function drawFrame(): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = getViewport();
    // clearRect always runs to wipe stale pixels from previous frames
    ctx.clearRect(0, 0, width, height);

    if (document.hidden) return;

    const wind = getWind();
    if (!wind) return; // no wind data — canvas stays clear

    // Adjust wind direction for current map bearing so streamlets flow in the
    // correct screen direction after map rotation (Fix 3 — ADR bearing correction).
    const effectiveDeg = (wind.degFrom - bearing + 360) % 360;
    const { dx, dy } = directionVectorFromDeg(effectiveDeg);
    const baseSpeed = computeStreamletBaseSpeed(wind.speedMph);

    // Viewport length cap applied per frame (Fix 1 — mobile density).
    const viewportLengthCap = Math.floor(width * VIEWPORT_LENGTH_CAP_RATIO);

    {
      // Call getFavorableStandPositions once per frame (NOT per streamlet).
      // This is inside the document-visible block — battery-saver: not called when hidden.
      // Positions are intentionally recomputed per invocation to track map pan/zoom.
      const favorableStands = opts.getFavorableStandPositions?.() ?? [];

      ctx.lineWidth = STREAMLET_LINE_WIDTH;
      ctx.lineCap = 'round';
      ctx.shadowBlur = STREAMLET_SHADOW_BLUR;
      ctx.shadowColor = STREAMLET_SHADOW_COLOR;

      // Set dash pattern once before the loop. Per-streamlet dashOffset is applied
      // inside the loop so each streamlet animates independently (no initial burst).
      ctx.setLineDash(STREAMLET_DASH_PATTERN);

      for (let i = 0; i < streamlets.length; i++) {
        const s = streamlets[i];

        // Assign speed on first use or after spawn (traveled reset)
        if (s.traveled === 0) {
          s.speed = baseSpeed * randomBetween(SPEED_JITTER_MIN, SPEED_JITTER_MAX);
        }

        // Per-frame line length driven by wind speed, capped to viewport width fraction
        s.length = Math.min(computeStreamletLineLength(wind.speedMph), viewportLengthCap);

        // If base speed is 0 (wind < 3 mph threshold), skip drawing entirely — do NOT draw a static line
        if (baseSpeed === 0) {
          // Advance traveled so stagger still works, but skip draw
          continue;
        }

        // Advance per-streamlet dash offset for independent marching-ants animation (Fix 2).
        s.dashOffset -= baseSpeed;
        ctx.lineDashOffset = s.dashOffset;

        // Head: s.x, s.y is the leading tip of the streamlet (moves in wind direction).
        // Tail: trails behind by the streamlet's length along the wind vector.
        const headX = s.x;
        const headY = s.y;
        const tailX = headX - dx * s.length;
        const tailY = headY - dy * s.length;

        // Check if this streamlet's head is near any favorable stand
        // (screen-space, zoom-invariant — favorableStands updated per-frame via closure)
        let isFavorable = false;
        for (const stand of favorableStands) {
          // Guard against off-canvas positions before distance check
          if (
            stand.x >= -FAVORABILITY_GLOW_RADIUS_PX &&
            stand.x <= width + FAVORABILITY_GLOW_RADIUS_PX &&
            stand.y >= -FAVORABILITY_GLOW_RADIUS_PX &&
            stand.y <= height + FAVORABILITY_GLOW_RADIUS_PX
          ) {
            const dx2 = headX - stand.x;
            const dy2 = headY - stand.y;
            if (dx2 * dx2 + dy2 * dy2 <= FAVORABILITY_GLOW_RADIUS_PX * FAVORABILITY_GLOW_RADIUS_PX) {
              isFavorable = true;
              break;
            }
          }
        }

        const headAlpha = isFavorable ? STREAMLET_FAVORABILITY_ALPHA : STREAMLET_HEAD_ALPHA;

        // Per-streamlet tapered gradient: tail is transparent, head shows at headAlpha
        const gradient = ctx.createLinearGradient(tailX, tailY, headX, headY);
        gradient.addColorStop(0, `rgba(${STREAMLET_HEAD_COLOR_BASE},0)`);
        gradient.addColorStop(1, `rgba(${STREAMLET_HEAD_COLOR_BASE},${headAlpha})`);

        ctx.strokeStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(headX, headY);
        ctx.stroke();

        // Advance position
        s.x += dx * s.speed;
        s.y += dy * s.speed;
        s.traveled += s.speed;

        // Despawn when off-canvas or maxTravel reached
        const offCanvas =
          s.x < -STREAMLET_MAX_LENGTH_PX || s.x > width + STREAMLET_MAX_LENGTH_PX ||
          s.y < -STREAMLET_MAX_LENGTH_PX || s.y > height + STREAMLET_MAX_LENGTH_PX;

        if (s.traveled >= s.maxTravel || offCanvas) {
          // Respawn at same grid cell
          const cellSize = computeGridCellSize(width, height);
          const colCount = Math.ceil(width / cellSize);
          const col = Math.floor(i % colCount);
          const row = Math.floor(i / colCount);
          streamlets[i] = spawnStreamletAtCell(col, row, /* stagger */ false);
          streamlets[i].speed = baseSpeed * randomBetween(SPEED_JITTER_MIN, SPEED_JITTER_MAX);
        }
      }

      // Reset dash and shadow after loop
      ctx.setLineDash([]);
      ctx.shadowBlur = 0;
    }
  }

  function loop(): void {
    if (!running) return;
    drawFrame();
    rafId = requestAnimationFrame(loop);
  }

  // ---------------------------------------------------------------------------
  // Public interface
  // ---------------------------------------------------------------------------

  function start(): void {
    if (running) return;
    running = true;
    if (streamlets.length === 0) initStreamlets();
    rafId = requestAnimationFrame(loop);
  }

  function stop(): void {
    running = false;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const { width, height } = getViewport();
      ctx.clearRect(0, 0, width, height);
    }
  }

  function resize(width: number, height: number): void {
    canvas.width = width;
    canvas.height = height;
    // Recompute grid for new dimensions
    initStreamlets();
  }

  function updateWind(): void {
    // Wind is fetched fresh each frame via getWind(). This is a no-op hook
    // available for callers that want to signal a state change explicitly.
    // Kept in public API for testability and future batching optimizations.
  }

  function isRunning(): boolean {
    return running;
  }

  function getStreamletCount(): number {
    return streamlets.length;
  }

  function updateBearing(deg: number): void {
    bearing = deg;
  }

  return { start, stop, resize, updateWind, isRunning, getStreamletCount, updateBearing };
}
